import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ModalProvider } from './contexts/ModalContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { QueryProvider } from './contexts/QueryContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import DashboardLayout from './components/Layout/Dashboard';
import ModalManager from './components/Modal/ModalManager';
import ToastContainer from './components/Toast/ToastContainer';
import HelpWidget from './components/Help/HelpWidget';
import PlatformTour from './components/Tour/PlatformTour';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Sites from './pages/Sites';
import SiteDetail from './pages/SiteDetail';
import Certificates from './pages/Certificates';
import Clients from './pages/Clients';
import Invoices from './pages/Invoices';
import Quotes from './pages/Quotes';
import PriceBook from './pages/PriceBook';
import Communication from './pages/Communication';
import AccountSettings from './pages/AccountSettings';
import CertificateTemplateWizard from './pages/CertificateTemplateWizard';
import CertificateTemplatesAdmin from './pages/CertificateTemplatesAdmin';
import CertificateWizard from './pages/CertificateWizard';
import Documentation from './pages/Documentation';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import SignUpSuccess from './pages/SignUpSuccess';
import Onboarding from './pages/Onboarding';
import EmailVerification from './pages/EmailVerification';
import LandingPage from './pages/LandingPage';
import PublicQuote from './pages/PublicQuote';
import PublicInvoice from './pages/PublicInvoice';
import ClientLogin from './pages/ClientLogin';
import ClientSetup from './pages/ClientSetup';
import ClientDashboard from './pages/ClientDashboard';
import AuthRedirect from './components/AuthRedirect';
import EngineerLayout from './pages/engineer/EngineerLayout';
import EngineerRoute from './pages/engineer/EngineerRoute';
import EngineerLogin from './pages/engineer/EngineerLogin';
import EngineerSetup from './pages/engineer/EngineerSetup';
import EngineerToday from './pages/engineer/EngineerToday';
import EngineerUpcoming from './pages/engineer/EngineerUpcoming';
import EngineerJobDetail from './pages/engineer/EngineerJobDetail';
import EngineerCertificateWizard from './pages/engineer/EngineerCertificateWizard';
import EngineerJobsList from './pages/engineer/EngineerJobsList';
import EngineerCertificates from './pages/engineer/EngineerCertificates';
import EngineerQuotesList from './pages/engineer/EngineerQuotesList';
import EngineerQuoteBuilder from './pages/engineer/EngineerQuoteBuilder';
import EngineerQuoteDetail from './pages/engineer/EngineerQuoteDetail';
import EngineerMessages from './pages/engineer/EngineerMessages';
import EngineerNotifications from './pages/engineer/EngineerNotifications';
import EngineerDocuments from './pages/engineer/EngineerDocuments';
import EngineerProfile from './pages/engineer/EngineerProfile';
import EngineerSettings from './pages/engineer/EngineerSettings';
import EngineerRouteView from './pages/engineer/EngineerRouteView';
import EngineerComingSoon from './pages/engineer/EngineerComingSoon';

function App() {
  const [isTourActive, setIsTourActive] = useState(false);

  const startTour = () => {
    setIsTourActive(true);
  };

  const completeTour = () => {
    setIsTourActive(false);
  };

  return (
    <QueryProvider>
      <ToastProvider>
        <ModalProvider>
          <Router>
            <AuthProvider>
              <NotificationProvider>
            <Routes>
              {/* Public Routes - No Authentication Required */}
              <Route path="/" element={
                <AuthRedirect>
                  <LandingPage />
                </AuthRedirect>
              } />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/signup/success" element={<SignUpSuccess />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/docs" element={<Documentation />} />
              <Route path="/q/:token" element={<PublicQuote />} />
              <Route path="/invoice/:token" element={<PublicInvoice />} />

              {/* Client portal */}
              <Route path="/client/login" element={<ClientLogin />} />
              <Route path="/client/setup" element={<ClientSetup />} />
              <Route path="/client/dashboard" element={<ClientDashboard />} />
              <Route path="/client" element={<Navigate to="/client/dashboard" replace />} />

              {/* Engineer PWA (separate auth, separate token) */}
              <Route path="/engineer/login" element={<EngineerLogin />} />
              <Route path="/engineer/setup/:token" element={<EngineerSetup />} />
              <Route
                path="/engineer"
                element={
                  <EngineerRoute>
                    <EngineerLayout />
                  </EngineerRoute>
                }
              >
                <Route index element={<Navigate to="/engineer/today" replace />} />
                <Route path="today" element={<EngineerToday />} />
                <Route path="upcoming" element={<EngineerUpcoming />} />
                <Route path="route" element={<EngineerRouteView />} />
                <Route path="jobs" element={<EngineerJobsList />} />
                <Route path="jobs/:id" element={<EngineerJobDetail />} />
                <Route
                  path="jobs/:jobId/certificates/new"
                  element={<EngineerCertificateWizard />}
                />
                <Route
                  path="jobs/:jobId/certificates/new/:templateId"
                  element={<EngineerCertificateWizard />}
                />
                <Route path="certificates" element={<EngineerCertificates />} />
                <Route path="quotes" element={<EngineerQuotesList />} />
                <Route path="quotes/new" element={<EngineerQuoteBuilder />} />
                <Route path="quotes/:id" element={<EngineerQuoteDetail />} />
                <Route
                  path="invoices"
                  element={
                    <EngineerComingSoon
                      title="Invoices & payments"
                      subtitle="Issue invoices on site and take card payments."
                      description="Coming soon: issue the final invoice on completion and take card payment via Stripe."
                    />
                  }
                />
                <Route path="messages" element={<EngineerMessages />} />
                <Route path="notifications" element={<EngineerNotifications />} />
                <Route path="documents" element={<EngineerDocuments />} />
                <Route path="profile" element={<EngineerProfile />} />
                <Route path="settings" element={<EngineerSettings />} />
              </Route>

              {/* Solo users live in the same workspace as admins — the
                  nav is filtered client-side to hide team-only features. */}
              <Route path="/solo-dashboard" element={<Navigate to="/dashboard" replace />} />

              {/* Primary workspace - ADMIN, ENGINEER (office mode), SOLO */}
              <Route path="/dashboard/*" element={
                <ProtectedRoute allowedRoles={['ADMIN', 'ENGINEER', 'SOLO']}>
                  <DashboardLayout>
                    <Routes>
                      <Route index element={<Dashboard />} />
                      <Route path="jobs" element={<Jobs />} />
                      <Route path="sites" element={<Sites />} />
                      <Route path="sites/:id" element={<SiteDetail />} />
                      <Route path="certificates" element={<Certificates />} />
                      <Route path="certificates/new" element={<CertificateWizard />} />
                      <Route path="clients" element={<Clients />} />
                      <Route path="invoices" element={<Invoices />} />
                      <Route path="quotes" element={<Quotes />} />
                      <Route path="price-book" element={<PriceBook />} />
                      <Route path="communication" element={<Communication />} />
                      <Route path="settings" element={<AccountSettings />} />
                      <Route path="settings/certificate-templates" element={<CertificateTemplatesAdmin />} />
                      <Route path="settings/certificate-templates/new" element={<CertificateTemplateWizard />} />
                      <Route path="team" element={<Dashboard />} />
                      <Route path="integrations" element={<Dashboard />} />
                      <Route path="support" element={<Dashboard />} />
                      <Route path="profile" element={<Navigate to="/dashboard/settings?tab=general" replace />} />
                    </Routes>
                    <ModalManager />
                    <ToastContainer />
                    <HelpWidget onStartTour={startTour} />
                    <PlatformTour 
                      isActive={isTourActive}
                      onComplete={completeTour}
                      onSkip={completeTour}
                    />
                  </DashboardLayout>
                </ProtectedRoute>
              } />

              {/* Redirect individual routes to dashboard routes */}
              <Route path="/jobs" element={<Navigate to="/dashboard/jobs" replace />} />
              <Route path="/sites" element={<Navigate to="/dashboard/sites" replace />} />
              <Route path="/sites/:id" element={<Navigate to="/dashboard/sites/:id" replace />} />
              <Route path="/clients" element={<Navigate to="/dashboard/clients" replace />} />
              <Route path="/certificates" element={<Navigate to="/dashboard/certificates" replace />} />
              <Route path="/invoices" element={<Navigate to="/dashboard/invoices" replace />} />
              <Route path="/quotes" element={<Navigate to="/dashboard/quotes" replace />} />
            </Routes>
              </NotificationProvider>
            </AuthProvider>
          </Router>
        </ModalProvider>
      </ToastProvider>
    </QueryProvider>
  );
}

export default App;
